import { Component, inject, signal, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, FormGroup, ReactiveFormsModule, Validators, AbstractControl, ValidationErrors } from '@angular/forms';
import { Router, ActivatedRoute, RouterLink } from '@angular/router';
import { InputerrorsComponent } from '../../inputerrors.component';
import { AuthService } from '../services/auth.service';

@Component({
  selector: 'app-auth-reset-password',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, RouterLink, InputerrorsComponent],
  template: `
    <div class="min-h-screen flex flex-col items-center justify-center bg-benzGrey-50 px-4 py-8">
      <div class="w-full max-w-md">
        
        <!-- Card -->
        <div class="bg-white rounded-2xl shadow-lg p-8">
          
          @if (!passwordReset()) {
            <!-- Reset Password Form -->
            <div>
              <!-- Header -->
              <div class="text-center mb-8">
                <div class="mx-auto w-16 h-16 bg-benzRed-100 rounded-full flex items-center justify-center mb-4">
                  <svg class="w-8 h-8 text-benzRed-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"></path>
                  </svg>
                </div>
                <h1 class="text-3xl font-bold text-benzGrey-900 mb-2">Neues Passwort</h1>
                <p class="text-benzGrey-600">
                  Geben Sie Ihr neues Passwort ein
                </p>
              </div>

              <!-- Error Messages -->
              @if (errorMessage()) {
                <div class="mb-6 p-4 bg-benzRed-50 border border-benzRed-200 rounded-lg" role="alert">
                  <div class="flex items-start gap-3">
                    <svg class="w-5 h-5 text-benzRed-700 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd"></path>
                    </svg>
                    <p class="text-sm text-benzRed-700">{{ errorMessage() }}</p>
                  </div>
                </div>
              }

              <!-- Token Invalid Message -->
              @if (tokenInvalid()) {
                <div class="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <div class="flex items-start gap-3">
                    <svg class="w-5 h-5 text-yellow-700 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd"></path>
                    </svg>
                    <div class="flex-1">
                      <p class="text-sm text-yellow-800 font-semibold mb-1">Link ungültig oder abgelaufen</p>
                      <p class="text-sm text-yellow-700">
                        Dieser Link ist nicht mehr gültig. Bitte fordern Sie einen neuen Link an.
                      </p>
                      <a
                        [routerLink]="['/auth/forgot-password']"
                        class="inline-block mt-2 text-sm text-yellow-800 font-semibold hover:underline">
                        Neuen Link anfordern →
                      </a>
                    </div>
                  </div>
                </div>
              }

              <!-- Form -->
              <form [formGroup]="resetPasswordForm" (ngSubmit)="resetPassword()" class="flex flex-col gap-4">
                
                <!-- New Password -->
                <app-inputerrors [control]="resetPasswordForm.controls.password">
                  <label class="flex flex-col gap-2">
                    <span class="text-sm font-medium text-benzGrey-900">
                      Neues Passwort <span class="text-benzRed-700">*</span>
                    </span>
                    <div class="relative">
                      <input
                        [type]="showPassword ? 'text' : 'password'"
                        formControlName="password"
                        placeholder="Mindestens 8 Zeichen"
                        [class.border-benzRed-700]="resetPasswordForm.controls.password.touched && !resetPasswordForm.controls.password.valid"
                        class="w-full border-2 border-benzGrey-900 placeholder:font-light placeholder:text-benzGrey-600 rounded-lg px-4 py-3 pr-12 focus:outline-none focus:ring-2 focus:ring-benzRed-700 transition-all"
                        required>
                      <button
                        type="button"
                        (click)="togglePasswordVisibility()"
                        class="absolute right-3 top-1/2 -translate-y-1/2 text-benzGrey-600 hover:text-benzGrey-900 focus:outline-none focus:ring-2 focus:ring-benzRed-700 rounded p-1">
                        @if (showPassword) {
                          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"></path>
                          </svg>
                        } @else {
                          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path>
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"></path>
                          </svg>
                        }
                      </button>
                    </div>
                  </label>
                </app-inputerrors>

                <!-- Confirm Password -->
                <app-inputerrors [control]="resetPasswordForm.controls.confirmPassword">
                  <label class="flex flex-col gap-2">
                    <span class="text-sm font-medium text-benzGrey-900">
                      Passwort bestätigen <span class="text-benzRed-700">*</span>
                    </span>
                    <input
                      [type]="showPassword ? 'text' : 'password'"
                      formControlName="confirmPassword"
                      placeholder="Passwort wiederholen"
                      [class.border-benzRed-700]="resetPasswordForm.controls.confirmPassword.touched && !resetPasswordForm.controls.confirmPassword.valid"
                      class="border-2 border-benzGrey-900 placeholder:font-light placeholder:text-benzGrey-600 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-benzRed-700 transition-all"
                      required>
                  </label>
                </app-inputerrors>

                <!-- Password Requirements -->
                <div class="p-3 bg-benzGrey-50 rounded-lg">
                  <p class="text-xs font-semibold text-benzGrey-700 mb-2">Passwort-Anforderungen:</p>
                  <ul class="text-xs text-benzGrey-600 space-y-1">
                    <li class="flex items-center gap-2">
                      <svg class="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                        <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"></path>
                      </svg>
                      Mindestens 8 Zeichen
                    </li>
                    <li class="flex items-center gap-2">
                      <svg class="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                        <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"></path>
                      </svg>
                      Beide Passwörter müssen übereinstimmen
                    </li>
                  </ul>
                </div>

                <!-- Submit Button -->
                <button
                  type="submit"
                  [disabled]="isLoading() || !resetPasswordForm.valid || tokenInvalid()"
                  class="w-full mt-2 px-6 py-4 bg-benzRed-700 text-white text-lg font-semibold rounded-lg hover:bg-benzRed-800 disabled:bg-benzGrey-300 disabled:cursor-not-allowed transition-colors focus:outline-none focus:ring-4 focus:ring-benzRed-300">
                  @if (isLoading()) {
                    <span class="flex items-center justify-center gap-2">
                      <svg class="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <span>Wird gespeichert...</span>
                    </span>
                  } @else {
                    <span>Passwort zurücksetzen</span>
                  }
                </button>
              </form>
            </div>
          } @else {
            <!-- Success Message -->
            <div class="text-center">
              <!-- Success Icon -->
              <div class="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                <svg class="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
                </svg>
              </div>

              <h2 class="text-2xl font-bold text-benzGrey-900 mb-3">Passwort erfolgreich geändert!</h2>
              <p class="text-benzGrey-600 mb-6">
                Ihr Passwort wurde erfolgreich zurückgesetzt. Sie können sich jetzt mit Ihrem neuen Passwort anmelden.
              </p>

              <button
                type="button"
                (click)="goToLogin()"
                class="w-full px-6 py-4 bg-benzRed-700 text-white text-lg font-semibold rounded-lg hover:bg-benzRed-800 transition-colors focus:outline-none focus:ring-4 focus:ring-benzRed-300">
                Zur Anmeldung
              </button>
            </div>
          }
        </div>
      </div>
    </div>
  `,
  styles: [`
    :host {
      display: block;
    }
  `]
})
export class AuthResetPasswordComponent implements OnInit {
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private authService = inject(AuthService);

  public resetPasswordForm = new FormGroup({
    password: new FormControl('', [Validators.required, Validators.minLength(8)]),
    confirmPassword: new FormControl('', [Validators.required]),
  }, { validators: this.passwordMatchValidator });

  public isLoading = signal(false);
  public errorMessage = signal<string | null>(null);
  public passwordReset = signal(false);
  public showPassword = false;
  public tokenInvalid = signal(false);
  private resetToken = signal<string | null>(null);

  ngOnInit(): void {
    // Get reset token from URL query params
    this.route.queryParams.subscribe(params => {
      const token = params['token'];
      if (token) {
        this.resetToken.set(token);
        // Validate token with backend
        this.authService.validateResetToken(token).subscribe({
          next: (response) => {
            if (!response.valid) {
              this.tokenInvalid.set(true);
            }
          },
          error: () => {
            this.tokenInvalid.set(true);
          }
        });
      } else {
        this.tokenInvalid.set(true);
      }
    });
  }

  passwordMatchValidator(control: AbstractControl): ValidationErrors | null {
    const password = control.get('password');
    const confirmPassword = control.get('confirmPassword');

    if (!password || !confirmPassword) {
      return null;
    }

    return password.value === confirmPassword.value ? null : { passwordMismatch: true };
  }

  resetPassword(): void {
    if (!this.resetPasswordForm.valid || this.tokenInvalid()) {
      this.resetPasswordForm.markAllAsTouched();
      return;
    }

    this.isLoading.set(true);
    this.errorMessage.set(null);

    const password = this.resetPasswordForm.controls.password.value!;
    const token = this.resetToken();

    if (!token) {
      this.errorMessage.set('Ungültiger Reset-Token');
      this.isLoading.set(false);
      return;
    }

    this.authService.resetPassword(token, password).subscribe({
      next: () => {
        this.passwordReset.set(true);
        this.isLoading.set(false);
      },
      error: (error) => {
        this.errorMessage.set(error.message || 'Fehler beim Zurücksetzen des Passworts');
        this.isLoading.set(false);
      }
    });
  }

  togglePasswordVisibility(): void {
    this.showPassword = !this.showPassword;
  }

  goToLogin(): void {
    this.router.navigate(['/auth/login']);
  }
}

